import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { register } from '../api/auth';
import './RegisterPage.css';

// Import logo
import luminaLogo from '../assets/luminalogo3.png';

const RegisterPage = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Basic validation
        if (formData.password !== formData.confirmPassword) {
            setError('Passwörter stimmen nicht überein!');
            return;
        }

        if (formData.password.length < 6) {
            setError('Das Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }

        if (!agreedToTerms) {
            setError('Bitte stimmen Sie den Allgemeinen Geschäftsbedingungen zu');
            return;
        }

        setLoading(true);

        try {
            const response = await register({
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                password: formData.password,
                confirmPassword: formData.confirmPassword
            });

            alert(response.message || 'Registrierung erfolgreich!');

            navigate('/login');

        } catch (err) {
            console.error('Registration error:', err);

            const errorMessage = err?.response?.data;
            if (typeof errorMessage === 'string') {
                setError(errorMessage);
            } else {
                setError('Registrierung fehlgeschlagen. Die E-Mail-Adresse wird möglicherweise bereits verwendet.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="register-page">
            {/* Background particles */}
            <div className="register-particles">
                <div className="particles-glow" />
                {[...Array(20)].map((_, index) => (
                    <div key={index} className="particle" />
                ))}
            </div>

            {/* Main Container */}
            <motion.div
                className="register-container"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                {/* Logo */}
                <motion.div
                    className="register-logo"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.2 }}
                >
                    <img src={luminaLogo} alt="Lumina Logo" />
                </motion.div>

                {/* Divider */}
                <motion.div
                    className="register-divider"
                    initial={{ width: 0 }}
                    animate={{ width: 80 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                />

                {/* Title */}
                <motion.h1
                    className="register-title"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                >
                    Konto erstellen
                </motion.h1>

                <motion.p
                    className="register-subtitle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                >
                    Werden Sie Teil von Lumina und bewahren Sie Erinnerungen für immer
                </motion.p>

                {/* Error Message */}
                {error && (
                    <motion.div
                        className="error-message"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            color: error.includes('erfolgreich') ? '#51cf66' : '#ff6b6b',
                            backgroundColor: error.includes('erfolgreich')
                                ? 'rgba(81, 207, 102, 0.1)'
                                : 'rgba(255, 107, 107, 0.1)',
                            padding: '12px 20px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: error.includes('erfolgreich')
                                ? '1px solid rgba(81, 207, 102, 0.3)'
                                : '1px solid rgba(255, 107, 107, 0.3)'
                        }}
                    >
                        {error}
                    </motion.div>
                )}

                {/* Register Form */}
                <motion.form
                    className="register-form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.7 }}
                >
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="firstName">Vorname</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                placeholder="Max"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="lastName">Nachname</label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                placeholder="Mustermann"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">E-Mail-Adresse</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            placeholder="ihre@email.de"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Passwort</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                name="password"
                                placeholder="Erstellen Sie ein sicheres Passwort"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                minLength="6"
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={loading}
                            >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        <small className="password-hint">Mindestens 6 Zeichen</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Passwort bestätigen</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                name="confirmPassword"
                                placeholder="Passwort erneut eingeben"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                disabled={loading}
                            >
                                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                    </div>

                    <div className="terms-checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={agreedToTerms}
                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                required
                                disabled={loading}
                            />
                            <span>
                                Ich stimme den{' '}
                                <button type="button" onClick={() => navigate('/terms')} disabled={loading}>
                                    Allgemeinen Geschäftsbedingungen
                                </button>{' '}
                                und der{' '}
                                <button type="button" onClick={() => navigate('/privacy')} disabled={loading}>
                                    Datenschutzerklärung
                                </button>
                                {' '}zu
                            </span>
                        </label>
                    </div>

                    <motion.button
                        type="submit"
                        className="register-submit-btn"
                        whileHover={!loading ? { scale: 1.02, y: -2 } : {}}
                        whileTap={!loading ? { scale: 0.98 } : {}}
                        disabled={loading}
                    >
                        {loading ? 'Konto wird erstellt…' : 'Konto erstellen'}
                    </motion.button>
                </motion.form>

                {/* Login Link */}
                <motion.div
                    className="login-link"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.9 }}
                >
                    <p>
                        Sie haben bereits ein Konto?{' '}
                        <button onClick={() => navigate('/login')} disabled={loading}>
                            Anmelden
                        </button>
                    </p>
                </motion.div>

                {/* Back to Home */}
                <motion.button
                    className="back-home-link"
                    onClick={() => navigate('/')}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={loading}
                >
                    ← Zur Startseite
                </motion.button>
            </motion.div>
        </div>
    );
};

export default RegisterPage;
